import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAuth, useUser } from "@clerk/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useApi() {
  const { getToken } = useAuth();
  return useCallback(
    async (method, path, data) => {
      const token = await getToken();
      const res = await axios({ method, url: `${API}${path}`, data, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      return res.data;
    },
    [getToken]
  );
}

export function useFetch(path, { interval } = {}) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!path) return;
    try {
      setData(await api("get", path));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [api, path]);

  useEffect(() => {
    setLoading(true);
    load();
    if (!interval) return undefined;
    const id = setInterval(load, interval);
    return () => clearInterval(id);
  }, [load, interval]);

  return { data, loading, error, reload: load, setData };
}

export const errMsg = (e) => e?.response?.data?.detail || "Une erreur est survenue";

const ProfileCtx = createContext({ profile: null, reload: () => {} });

export function ProfileProvider({ children }) {
  const api = useApi();
  const { user, isLoaded } = useUser();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!isLoaded || !user) return;
    api("post", "/me/sync", {
      name: user.fullName || user.firstName || undefined,
      email: user.primaryEmailAddress?.emailAddress,
      avatar: user.hasImage ? user.imageUrl : undefined,
    }).then(setProfile).catch(() => {});
  }, [api, user, isLoaded]);

  const reload = useCallback(() => api("get", "/me").then(setProfile), [api]);
  return <ProfileCtx.Provider value={{ profile, setProfile, reload }}>{children}</ProfileCtx.Provider>;
}

export const useProfile = () => useContext(ProfileCtx);
